import type { DevChatCard, DevChatDetail, DevChatFeatures, DevOverview } from '#/services/dev-console/index.js'
import { getDevChat, getDevOverview } from './api.js'
import { clear, el } from './dom.js'
import { aiShieldIcon, chevronIcon, generalIcon, sectionIcon } from './icons.js'
import { t } from './labels.js'
import { openSheet, sectionHead } from './sheet.js'
import { planStats, statsRowEl } from './stats.js'

// The owner console view (`?view=dev` — plan: owner-console.md): the cross-group, READ-ONLY face of
// GET /api/dev/*. Anomaly-first layout (D5): status strip → live locks → AI Shield → the chat cards →
// the departed accordion; the detail sheet (D8) is where the ONE live probe happens. Split like
// stats.ts: the plan*() shapers are PURE (unit-tested); the render side is thin DOM transport
// (live-verify only). Every string renders through el() text nodes — group titles are
// attacker-influenced, so no innerHTML anywhere (the dom.ts contract).

// ---------------------------------------------------------------------------------------------------
// PURE shaping
// ---------------------------------------------------------------------------------------------------

export interface StripAnomaly {
  /** raid | night | notAdmin | degraded — the emoji/wording picker. */
  kind: 'raid' | 'night' | 'notAdmin' | 'degraded'
  title: string
  /** Extra display value (a clock time for raid, the stored status for notAdmin). */
  extra?: string
}

/**
 * PURE: the status strip's anomaly list, in severity order (degraded service first, then live locks,
 * then stale-admin tell-tales). Empty = the single green line. The roster rows carry LAST KNOWN state
 * (D5) — the notAdmin entries say so via the shared «ultimo stato noto» wording, never "is".
 */
export function planStrip(o: DevOverview): StripAnomaly[] {
  const out: StripAnomaly[] = []
  if (!o.ready.ready)
    out.push({ kind: 'degraded', title: [o.ready.checks.postgres ? '' : 'PostgreSQL', o.ready.checks.redis ? '' : 'Redis'].filter(Boolean).join(' + ') })
  for (const l of o.live.raid)
    out.push({ kind: 'raid', title: l.title, ...(l.restoreAt !== undefined ? { extra: l.restoreAt } : {}) })
  for (const l of o.live.night)
    out.push({ kind: 'night', title: l.title })
  for (const c of o.chats) {
    if (c.status !== 'administrator')
      out.push({ kind: 'notAdmin', title: c.title, extra: c.status })
  }
  return out
}

/** The card chips: one emoji per feature that is ON, in a fixed order (the sheet spells them out). */
export function chips(f: DevChatFeatures): string {
  const parts: string[] = []
  if (f.aiShield)
    parts.push('🤖')
  if (f.captcha)
    parts.push('👋')
  if (f.antiFlood)
    parts.push('🌊')
  if (f.antiRaid)
    parts.push('🛡️')
  if (f.nightMode)
    parts.push('🌙')
  if (f.silent)
    parts.push('🔇')
  if (f.logChannel)
    parts.push('📡')
  return parts.join(' ')
}

/** The budget cell: spent/limit, with an UNKNOWN spend declared as a bare «n/d» — never rendered as 0 (D6). */
export function budgetValue(used: number | null, limit: number): string {
  return used === null ? 'n/d' : `${used}/${limit}`
}

/**
 * The COVERAGE line: what share of checkable traffic the classifier actually looked at today, plus the raw
 * volume behind it — `42% · 50/120 · 780`. The audit of 2026-07-30 measured this at ~8% in the busiest
 * group (50 checks against 500-900 messages/day) and it was invisible in every surface; a flag count with
 * no denominator reads as "quiet group" when it means "blind since 08:00".
 *
 * Honesty rules, deliberate: any unreadable counter renders the WHOLE line 'n/d' (a percentage computed
 * from a fabricated 0 would be a confident lie); zero checkable messages renders '—' rather than a
 * division by zero dressed up as 0%; and the ratio is capped at 100% because the two counters can race
 * (a classify billed just after midnight UTC against candidates counted before it).
 */
export function coverageValue(classified: number | null, candidates: number | null, seen: number | null): string {
  if (classified === null || candidates === null || seen === null)
    return 'n/d'
  if (candidates === 0)
    return `— · 0/0 · ${seen}`
  const pct = Math.min(100, Math.round((classified / candidates) * 100))
  return `${pct}% · ${classified}/${candidates} · ${seen}`
}

/** The feature rows the detail sheet spells out, labeled by the SHARED section/title keys (no new wording). */
export function featureRows(f: DevChatFeatures): Array<{ labelKey: string, on: boolean }> {
  return [
    { labelKey: 'cfg-sec-aishield', on: f.aiShield },
    { labelKey: 'cfg-sec-captcha', on: f.captcha },
    { labelKey: 'cfg-sec-flood', on: f.antiFlood },
    { labelKey: 'cfg-sec-raid', on: f.antiRaid },
    { labelKey: 'cfg-sec-nightmode', on: f.nightMode },
    { labelKey: 'ui-silent-title', on: f.silent },
    { labelKey: 'ui-logchannel-title', on: f.logChannel },
  ]
}

// ---------------------------------------------------------------------------------------------------
// DOM transport (live-verify only)
// ---------------------------------------------------------------------------------------------------

function row(label: string, value: string, valueClass = 'value'): HTMLElement {
  return el('div', { class: 'row' }, el('span', { class: 'label' }, label), el('span', { class: valueClass }, value))
}

function fmtDate(iso: string | null): string {
  return iso === null ? '—' : new Date(iso).toLocaleDateString()
}

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

function anomalyRow(a: StripAnomaly): HTMLElement {
  if (a.kind === 'degraded')
    return row(`⚠️ ${t('dev-status-degraded')}`, a.title, 'value stats-error')
  if (a.kind === 'raid')
    return row(`🛡️ ${a.title}`, a.extra !== undefined ? `${t('dev-live-raid')} → ${fmtTime(a.extra)}` : t('dev-live-raid'), 'value stats-error')
  if (a.kind === 'night')
    return row(`🌙 ${a.title}`, t('dev-live-night'))
  return row(`⚠️ ${a.title}`, `${a.extra ?? ''} · ${t('dev-last-known')}`, 'value stats-error')
}

function renderStrip(root: HTMLElement, o: DevOverview): void {
  root.append(sectionHead(generalIcon, 'dev-status-title', 'dev-status-body'))
  const group = el('div', { class: 'sec-group' })
  const anomalies = planStrip(o)
  group.append(row(
    o.ready.ready ? `✅ ${t('dev-status-ok')}` : `⚠️ ${t('dev-status-degraded')}`,
    '',
    'value',
  ))
  group.append(row(t('dev-active-groups'), String(o.chats.length)))
  for (const a of anomalies)
    group.append(anomalyRow(a))
  root.append(group)
}

function renderAiShield(root: HTMLElement, o: DevOverview): void {
  root.append(sectionHead(aiShieldIcon, 'cfg-sec-aishield', 'dev-ai-body'))
  const group = el('div', { class: 'sec-group' })
  if (!o.aiShieldArmed)
    group.append(row(`⚠️ ${t('dev-ai-disarmed')}`, '', 'value stats-error'))
  group.append(row(t('dev-ai-active-in'), `${o.aiShield.onCount}/${o.aiShield.activeCount}`))
  group.append(row(t('dev-flags-today'), String(o.aiShield.flagsToday)))
  group.append(row(t('dev-flags-7d'), String(o.aiShield.flags7d)))
  for (const c of o.aiShield.perChat) {
    const r = row(c.title, budgetValue(c.budgetUsedToday, c.budgetLimit))
    r.append(el('p', { class: 'hint' }, `${t('dev-budget-hint')} · ${t('dev-flags-today')}: ${c.flagsToday}`))
    // The denominator, right under the spend it explains: 50/50 next to "8% · 50/612 · 780" is the whole
    // recall story in one glance — the cap is full AND the shield saw one message in twelve.
    r.append(el('p', { class: 'hint' }, `${t('dev-coverage-hint')}: ${coverageValue(c.budgetUsedToday, c.candidatesToday, c.seenToday)}`))
    group.append(r)
  }
  root.append(group)
}

function chatCard(c: DevChatCard, onOpen: (card: DevChatCard, trigger: HTMLElement) => void): HTMLElement {
  const btn = el('button', { class: 'row row-tap', type: 'button' }) as HTMLButtonElement
  btn.append(el('span', { class: 'label' }, c.title))
  const badge = chips(c.features)
  btn.append(el('span', { class: 'value' }, badge))
  btn.append(chevronIcon('row-chevron'))
  btn.append(el('p', { class: 'hint' }, `${c.type} · ${fmtDate(c.joinedAt)} · ${c.language.toUpperCase()}`))
  btn.addEventListener('click', () => onOpen(c, btn))
  return btn
}

// The groups head reuses the trusted-section glyph (the people icon — the closest fit; no new icon).
const groupsIcon = (className: string): SVGSVGElement => sectionIcon('trusted', className)

function renderChats(root: HTMLElement, o: DevOverview, onOpen: (card: DevChatCard, trigger: HTMLElement) => void): void {
  root.append(sectionHead(groupsIcon, 'dev-groups-title', 'dev-groups-body'))
  const group = el('div', { class: 'sec-group' })
  for (const c of o.chats)
    group.append(chatCard(c, onOpen))
  if (o.chats.length === 0)
    group.append(row(t('dev-groups-empty'), ''))
  root.append(group)
  // Channels apart (service contract): no chips — the defenses the chips claim can't run in a channel.
  if (o.channels.length > 0) {
    const chGroup = el('div', { class: 'sec-group' })
    chGroup.append(row(t('dev-channels-title'), ''))
    for (const ch of o.channels)
      chGroup.append(row(`📣 ${ch.title}`, ch.status))
    root.append(chGroup)
  }
}

function renderFormer(root: HTMLElement, o: DevOverview): void {
  if (o.former.length === 0)
    return
  const details = el('details', { class: 'dev-former' })
  details.append(el('summary', {}, `${t('dev-former-title')} (${o.former.length})`))
  const group = el('div', { class: 'sec-group' })
  for (const f of o.former)
    group.append(row(f.title, `${f.status} · ${fmtDate(f.leftAt)}`))
  details.append(group)
  root.append(details)
}

// The detail sheet (D8): opens instantly from the card's data, then ONE fetch brings settings + stats +
// the live probe; the body is repainted in place when it settles (skipped if the sheet closed meanwhile).
function openChatSheet(card: DevChatCard, trigger: HTMLElement): void {
  const body = el('div', { class: 'dev-detail' })
  body.append(el('p', { class: 'sheet-body' }, `${card.type} · ${fmtDate(card.joinedAt)} · ${card.language.toUpperCase()}`))
  const probeArea = el('div', { class: 'sec-group' }, row(t('dev-probe-title'), t('dev-probe-loading')))
  body.append(probeArea)
  const featArea = el('div', { class: 'sec-group' })
  for (const f of featureRows(card.features))
    featArea.append(row(t(f.labelKey), f.on ? t('dev-on') : t('dev-off')))
  body.append(featArea)
  const statsArea = el('div')
  body.append(statsArea)
  openSheet(groupsIcon('sheet-icon'), card.title, [body], { bodyKey: 'dev-chat-help' }, trigger)

  void (async () => {
    let detail: DevChatDetail
    try {
      detail = await getDevChat(card.chatId)
    }
    catch {
      if (probeArea.isConnected) {
        clear(probeArea)
        probeArea.append(row(t('dev-probe-title'), t('dev-detail-error'), 'value stats-error'))
      }
      return
    }
    if (!body.isConnected)
      return // closed while loading — nothing to paint
    clear(probeArea)
    if (detail.probe.verified) {
      probeArea.append(row(t('dev-probe-bot'), detail.probe.botStatus))
      probeArea.append(row(t('dev-probe-members'), detail.probe.memberCount === null ? 'n/d' : String(detail.probe.memberCount)))
      if (detail.probe.ownerIsAdmin === true) {
        // D9: the deep-link appears ONLY on a PROVEN owner-admin (three-state — null/unknown hides it).
        // A same-origin navigation into the per-group dashboard (re-authorized server-side anyway); the
        // launch HASH is carried over — #tgWebAppData is what the SDK rebuilds initData from, and a bare
        // /?chat=… reload would depend on a sessionStorage restore some Telegram Desktop builds don't do
        // (the main.ts no-initData guard exists exactly for those). Cold-eye s76.
        const open = el('button', { class: 'lc-btn', type: 'button' }, t('dev-open-dashboard')) as HTMLButtonElement
        open.addEventListener('click', () => location.assign(`/?chat=${encodeURIComponent(card.chatId)}${location.hash}`))
        probeArea.append(el('div', { class: 'row' }, open))
      }
    }
    else {
      probeArea.append(row(t('dev-probe-title'), t('dev-probe-failed'), 'value stats-error'))
    }
    // Compact stats table — planStats over the SHARED bucket spec + the SHARED statsRowEl pairing, but
    // NOT renderStats: its section head carries a «?» info trigger, and a sheet can't host another
    // sheet's opener (one sheet at a time — tapping it would silently REPLACE this detail). A null
    // model (malformed reply) renders the SAME loud error line the stats section shows — planStats's
    // contract: never a silent half-picture.
    statsArea.append(el('h3', { class: 'sheet-sub' }, `${t('ui-stats-title')} — ${t('ui-stats-caption')}`))
    const model = planStats(detail.stats)
    if (model) {
      const group = el('div', { class: 'sec-group' })
      for (const r of model.rows)
        group.append(statsRowEl(t(r.labelKey), r.recent, r.month))
      if (model.other)
        group.append(statsRowEl(t('stats-row-other'), model.other.recent, model.other.month))
      statsArea.append(group)
    }
    else {
      // The renderSectionError row skeleton (error text in the label slot, danger-styled).
      statsArea.append(el('div', { class: 'sec-group' }, el('div', { class: 'row' }, el('span', { class: 'label stats-error' }, t('ui-stats-error')))))
    }
  })()
}

/**
 * Boot the console view into `app`: one overview fetch → full render. A load failure is the loud
 * full-screen error (the caller's messageKey already mapped 403 to «forbidden» — a non-owner who
 * somehow reaches the view sees exactly that, nothing else).
 */
export async function bootDevConsole(app: HTMLElement): Promise<{ ok: boolean, err?: unknown }> {
  let overview: DevOverview
  try {
    overview = await getDevOverview()
  }
  catch (err) {
    return { ok: false, err }
  }
  clear(app)
  app.classList.add('enter')
  app.append(el('div', { class: 'top-bar' }, el('div', { class: 'top-title' }, t('dev-title'))))
  const strip = el('div')
  renderStrip(strip, overview)
  app.append(strip)
  const ai = el('div')
  renderAiShield(ai, overview)
  app.append(ai)
  const chatsRoot = el('div')
  renderChats(chatsRoot, overview, openChatSheet)
  app.append(chatsRoot)
  renderFormer(app, overview)
  return { ok: true }
}
